import type {
  HealthComponentBreakdown,
  HealthSignalBreakdown,
} from "../../types/crm";

const SIGNAL_LABELS: Record<keyof HealthComponentBreakdown, string> = {
  sentiment: "Customer Sentiment",
  productPerf: "Product Performance",
  inAppRating: "In-App Rating",
  billing: "Billing",
  engagement: "Engagement",
  onboarding: "Onboarding",
};

const SIGNAL_ORDER: Array<keyof HealthComponentBreakdown> = [
  "sentiment",
  "productPerf",
  "inAppRating",
  "billing",
  "engagement",
  "onboarding",
];

interface Props {
  breakdown: HealthComponentBreakdown | null | undefined;
  /** Shown above the signal list; optional summary like "Score: 72 (Watch)". */
  headline?: string;
  /**
   * Rendering surface:
   *   - "popover" (default) — adds the floating-card chrome (shadow-lg,
   *     fixed width) used by the dashboard hover.
   *   - "inline" — drops the chrome so the component can slot into an
   *     existing card without visible nesting.
   */
  variant?: "popover" | "inline";
}

/**
 * Inline table summarizing each of the 6 health signal contributions.
 * Used in two places:
 *   - Dashboard: wrapped in a hover-popover from HealthBandChip (variant="popover")
 *   - 360 page: rendered as a static section under Health Score (variant="inline")
 */
export function HealthBreakdownPopover({
  breakdown,
  headline,
  variant = "popover",
}: Props) {
  if (!breakdown) {
    return (
      <div className="text-sm text-gray-500 italic">
        No breakdown available yet.
      </div>
    );
  }
  const wrapperCls =
    variant === "popover"
      ? "p-4 bg-white border border-gray-200 rounded-lg shadow-lg w-80 max-w-[calc(100vw-2rem)]"
      : "";
  return (
    <div className={wrapperCls}>
      {headline && (
        <div className="mb-3 text-sm font-semibold text-gray-800">
          {headline}
        </div>
      )}
      <table className="w-full text-xs table-fixed">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
            <th className="text-left py-1.5 font-medium">Signal</th>
            <th className="text-right py-1.5 font-medium w-16">Score</th>
          </tr>
        </thead>
        <tbody>
          {SIGNAL_ORDER.map((key) => {
            const sig: HealthSignalBreakdown = breakdown[key];
            return (
              <tr key={key} className="align-top border-b border-gray-50 last:border-0">
                <td className="py-2 pr-2 min-w-0">
                  <div className="font-medium text-gray-800">
                    {SIGNAL_LABELS[key]}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5 break-words whitespace-normal">
                    {sig.rationale}
                  </div>
                </td>
                <td className="text-right py-2 whitespace-nowrap align-top">
                  {sig.available && sig.value != null ? (
                    <span className="font-mono font-medium text-gray-800">
                      {sig.value}
                      <span className="text-gray-400">
                        /{sig.weight}
                      </span>
                    </span>
                  ) : (
                    <span className="text-gray-300 font-mono">— / {sig.weight}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
